// NPM packages
import React from 'react';
import { Alert, AlertTitle, useTheme } from '@mui/material';
import styled from '@emotion/styled';

// All other imports
import PageContent from 'components/core/PageContent';
import { useServices } from 'services';
import { observer } from 'mobx-react-lite';
import useSelectProjectByUrl from 'hooks/useSelectProjectByUrl';
import useResourceGuard from 'hooks/useResourceGuard';
import Project from 'types/Project';

import ProjectDetails from './components/ProjectDetails';

import H1 from 'components/core/H1';
import JoinButton from 'components/project/JoinButton';
import { TitleBar } from './styles';

const Root = styled(PageContent)({});

const PendingAlert = styled(Alert)({
  marginBottom: '1rem',
});

const StyledH1 = styled(H1)(({ theme: { palette } }) => ({
  color: palette.primary.main,
  marginBottom: 0,
  cursor: 'pointer',
}));

export interface ProjectInfoProps {
  className?: string;
}

function ProjectInfo(props: ProjectInfoProps): React.ReactElement | null {
  const { className } = props;
  const { projectService, authService } = useServices();
  useSelectProjectByUrl({ projectService });
  const guardContent = useResourceGuard({
    resource: projectService.selectedProject,
  });
  const theme = useTheme();

  let body: React.ReactNode;

  if (guardContent) {
    body = guardContent;
  } else {
    // We know the project is not null or a NotFoundError since
    // the guard returned null
    const project = projectService.selectedProject as Project;
    const isPending = authService.user?.isPending(project.slug);

    body = (
      <>
        {isPending && (
          <PendingAlert severity="info">
            <AlertTitle>Membership Awaiting Approval</AlertTitle>
            Your request to join {project.title} is pending approval.
          </PendingAlert>
        )}
        <div>
          <TitleBar>
            <div>
              <StyledH1>{project.title}</StyledH1>
            </div>
            <JoinButton project={project} size="medium" includeEdit includeMembers />
          </TitleBar>
          <br/>
          <ProjectDetails project={project} />
        </div>
      </>
    );
  }

  return (
    <Root className={className} maxWidth={theme.maxToolWidth}>
      {body}
    </Root>
  );
}

export default observer(ProjectInfo);
